import { useEffect, useState, useCallback } from 'react';
import { hubAPI } from '@/lib/api';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { BookMarked, Pencil, Trash2, AlertTriangle, Save, X, Layers, Info } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

/**
 * Section « Mes modèles » — permet à un utilisateur de renommer ou supprimer
 * les modèles de pipeline qu'il a créés lui-même (is_user_template + created_by=me).
 * Les modèles utilisés par au moins un projet ne peuvent pas être supprimés.
 */
export function MyTemplatesDialog({ open, onOpenChange, spaceId, onChange }) {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState(null); // template en cours d'édition
    const [editForm, setEditForm] = useState({ name: '', description: '' });
    const [saving, setSaving] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await hubAPI.myTemplates(spaceId || undefined);
            setTemplates(r.data?.templates || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur');
        } finally {
            setLoading(false);
        }
    }, [spaceId]);

    useEffect(() => {
        if (open) load();
    }, [open, load]);

    const startEdit = (t) => {
        setEditing(t);
        setEditForm({ name: t.name || '', description: t.description || '' });
    };

    const submitEdit = async () => {
        const name = (editForm.name || '').trim();
        if (!name) {
            toast.error('Le titre est obligatoire');
            return;
        }
        setSaving(true);
        try {
            await hubAPI.updateTheme(editing.id, {
                name,
                description: editForm.description || null,
            });
            toast.success('Modèle mis à jour');
            setEditing(null);
            await load();
            onChange?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Enregistrement impossible');
        } finally {
            setSaving(false);
        }
    };

    const doDelete = async (t) => {
        try {
            await hubAPI.deleteTheme(t.id);
            toast.success(`Modèle « ${t.name} » supprimé`);
            setConfirmDelete(null);
            await load();
            onChange?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Suppression impossible');
        }
    };

    return (
        <>
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="my-templates-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <BookMarked className="w-5 h-5 text-indigo-600" />
                            {editing ? 'Renommer le modèle' : 'Mes modèles de pipeline'}
                        </DialogTitle>
                        <DialogDescription>
                            {editing
                                ? "Modifie le titre et la description. Le pipeline (les étapes) reste inchangé — pour le modifier, ouvre un projet qui l'utilise et édite son pipeline."
                                : 'Les modèles que tu as sauvegardés depuis un projet. Renomme-les ou supprime-les ici. Un modèle utilisé par au moins un projet ne peut pas être supprimé (retire-le des projets concernés d\u2019abord).'}
                        </DialogDescription>
                    </DialogHeader>

                    {editing ? (
                        <div className="space-y-3 py-2">
                            <div>
                                <Label htmlFor="tpl-edit-title" className="text-sm">Titre *</Label>
                                <Input
                                    id="tpl-edit-title"
                                    autoFocus
                                    value={editForm.name}
                                    maxLength={80}
                                    onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                                    data-testid="tpl-edit-title-input"
                                />
                                <p className="text-[11px] text-slate-400 mt-1">{editForm.name.length}/80 caractères</p>
                            </div>
                            <div>
                                <Label htmlFor="tpl-edit-desc" className="text-sm">Description (facultatif)</Label>
                                <Textarea
                                    id="tpl-edit-desc"
                                    value={editForm.description}
                                    rows={3}
                                    onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
                                    data-testid="tpl-edit-desc-input"
                                />
                            </div>
                            <div className="rounded-md bg-slate-50 border border-slate-200 p-2 text-[11px] text-slate-700 flex items-start gap-2">
                                <Info className="w-3.5 h-3.5 shrink-0 mt-0.5 text-slate-500" />
                                <div>
                                    Ce modèle contient <strong>{editing.pipeline?.length || 0} étape{editing.pipeline?.length > 1 ? 's' : ''}</strong>.
                                    Créé le {editing.created_at ? new Date(editing.created_at).toLocaleDateString('fr-FR') : '—'}.
                                </div>
                            </div>
                        </div>
                    ) : loading ? (
                        <div className="text-center py-8 text-slate-400 text-sm">Chargement…</div>
                    ) : templates.length === 0 ? (
                        <div className="text-center py-10 text-slate-500">
                            <BookMarked className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                            <p className="text-sm">Tu n&apos;as pas encore de modèle enregistré.</p>
                            <p className="text-xs text-slate-400 mt-1">
                                Depuis un projet → ouvre l&apos;éditeur de pipeline → clic « Enregistrer comme modèle ».
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-2" data-testid="my-templates-list">
                            {templates.map((t) => (
                                <div
                                    key={t.id}
                                    className="border rounded-md p-3 flex items-start gap-3 hover:shadow-sm transition-all bg-white"
                                    data-testid={`my-template-row-${t.id}`}
                                >
                                    <div
                                        className="w-10 h-10 rounded-md flex items-center justify-center shrink-0 border"
                                        style={{ backgroundColor: (t.color || '#6366F1') + '15', borderColor: (t.color || '#6366F1') + '30' }}
                                    >
                                        <Layers className="w-4 h-4" style={{ color: t.color || '#6366F1' }} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="text-sm font-semibold text-slate-900 truncate">{t.name}</span>
                                            <Badge variant="secondary" className="text-[10px] bg-indigo-50 text-indigo-700 border-indigo-200">
                                                {t.pipeline?.length || 0} étape{t.pipeline?.length > 1 ? 's' : ''}
                                            </Badge>
                                            {t.projects_using_count > 0 && (
                                                <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">
                                                    {t.projects_using_count} projet{t.projects_using_count > 1 ? 's' : ''} l&apos;utilise{t.projects_using_count > 1 ? 'nt' : ''}
                                                </Badge>
                                            )}
                                        </div>
                                        {t.description && (
                                            <p className="text-xs text-slate-600 mt-0.5 line-clamp-2">{t.description}</p>
                                        )}
                                        <p className="text-[10px] text-slate-400 mt-1">
                                            Créé le {t.created_at ? new Date(t.created_at).toLocaleDateString('fr-FR') : '—'}
                                            {t.created_from_project_name ? ` depuis « ${t.created_from_project_name} »` : ''}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-1 shrink-0">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => startEdit(t)}
                                            title="Renommer"
                                            data-testid={`my-template-edit-${t.id}`}
                                        >
                                            <Pencil className="w-4 h-4" />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => setConfirmDelete(t)}
                                            className={cn('text-red-600 hover:bg-red-50', t.projects_using_count > 0 && 'opacity-40 cursor-not-allowed hover:bg-transparent')}
                                            disabled={t.projects_using_count > 0}
                                            title={t.projects_using_count > 0 ? "Modèle utilisé — impossible à supprimer" : "Supprimer"}
                                            data-testid={`my-template-delete-${t.id}`}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    <DialogFooter className="gap-2">
                        {editing ? (
                            <>
                                <Button variant="outline" onClick={() => setEditing(null)} disabled={saving} data-testid="tpl-edit-cancel">
                                    <X className="w-4 h-4 mr-1" /> Annuler
                                </Button>
                                <Button onClick={submitEdit} disabled={saving || !editForm.name.trim()} className="gap-1" data-testid="tpl-edit-save">
                                    <Save className="w-4 h-4" /> {saving ? 'Enregistrement…' : 'Enregistrer'}
                                </Button>
                            </>
                        ) : (
                            <Button variant="outline" onClick={() => onOpenChange(false)}>Fermer</Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Confirmation suppression */}
            <Dialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
                <DialogContent className="max-w-md" data-testid="my-template-delete-confirm-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-600">
                            <AlertTriangle className="w-5 h-5" /> Supprimer ce modèle ?
                        </DialogTitle>
                        <DialogDescription className="pt-2">
                            Le modèle <strong>« {confirmDelete?.name} »</strong> sera supprimé définitivement.
                            Les projets qui l&apos;utilisent déjà ne seront pas affectés (leur pipeline est copié), mais
                            il ne sera plus proposé lors de la création d&apos;un nouveau projet.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setConfirmDelete(null)}>Annuler</Button>
                        <Button variant="destructive" onClick={() => doDelete(confirmDelete)} data-testid="my-template-delete-confirm-btn">
                            <Trash2 className="w-4 h-4 mr-1" /> Supprimer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}

export default MyTemplatesDialog;
